import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ListingStageName } from '@/features/listing-stage/types';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { getDealListingsCount } from '../../helpers/get-deal-listings.count';
import { getStageListingsCount } from '../../helpers/get-stage-listings-count';
import { usePendingCompaniesCount } from '../../pending-companies/use-pending-companies-count.query';
import { useStageTabsConfig } from '../stage-management-table/use-stage-tabs-config';
import { useProjectListingsCount } from '../use-project-listings-count.query';
import { useProjectListingsTabParam } from './use-project-listings-tab-param';

export const useSplitListingStageTabDisplay = () => {
  const { t } = useTranslation('projects');
  const { hasRequiredPermission } = useUserPermissions();
  const { data: pendingCompaniesCount } = usePendingCompaniesCount();
  const tabStages = useStageTabsConfig();
  const { listingsTabParam } = useProjectListingsTabParam();
  const { data: listingsCount } = useProjectListingsCount();

  const tabFilters = useMemo(() => {
    return [
      {
        label: 'All',
        type: { id: 'all', name: 'all' },
        isActive: listingsTabParam === 'all',
        resultsQuantity: listingsCount?.total ?? 0,
      },
      ...tabStages.map(tab => ({
        label: tab.displayName ?? tab.name ?? '',
        type: tab,
        isActive: listingsTabParam === tab.id,
        resultsQuantity:
          tab.name === ListingStageName.deal
            ? getDealListingsCount(listingsCount?.stagesCount ?? [])
            : getStageListingsCount(tab, listingsCount?.stagesCount ?? []),
      })),
    ];
  }, [listingsCount, listingsTabParam, tabStages]);

  const mainPanelTabs = tabFilters.filter(
    tab => tab.type.name !== ListingStageName.notRelevant,
  );

  const secondaryPanelTabs = useMemo(() => {
    return [
      ...(hasRequiredPermission(PERMISSION.VIEW_PENDING_COMPANIES)
        ? [
            {
              isActive: listingsTabParam === 'pending-companies',
              resultsQuantity: pendingCompaniesCount?.pendingCompanies ?? 0,
              type: { id: 'pending-companies', name: 'pending-companies' },
              label: t('pendingCompanies.title'),
            },
          ]
        : []),
      ...tabFilters.filter(
        tab => tab.type.name === ListingStageName.notRelevant,
      ),
    ];
  }, [
    hasRequiredPermission,
    listingsTabParam,
    pendingCompaniesCount,
    tabFilters,
    t,
  ]);

  return {
    mainPanelTabs,
    secondaryPanelTabs,
  };
};
